import React from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { SharedOrderFormData } from './SharedFormSections';

export interface StoreData {
  CustomerNo: string;
  StoreName: string;
  Address: string;
  City: string;
  Prov: string;
  PostalCode: string;
  EmailAddress?: string;
  EmailAdddress?: string; // Note: typo in Firestore field name
  email?: string;
  Telephone: string;
  StoreNumber?: string;
  SortField?: string;
  SalesGroup?: string;
  SalespersonNo?: string;
  UDF_Outr_PickupNo?: string;
}

/**
 * Firebase service to fetch customer/store data
 */
export class CustomerDataService {
  /**
   * Fetch customer data from Firebase Firestore
   * @param storeId - The store ID to fetch data for
   * @returns Promise<SharedOrderFormData> - Customer data formatted for forms
   */
  static async fetchCustomerData(storeId: string): Promise<SharedOrderFormData> {
    try {
      const storeRef = doc(db, 'Stores', storeId);
      const storeSnap = await getDoc(storeRef);

      if (!storeSnap.exists()) {
        throw new Error(`Store with ID ${storeId} not found`);
      }

      const storeData = storeSnap.data() as StoreData;
      
      // Transform Firestore data to SharedOrderFormData format
      const customerData: SharedOrderFormData = {
        // Autopopulated fields from database
        customerNumber: storeData.CustomerNo || '',
        storeName: storeData.StoreName || '',
        address: storeData.Address || '',
        city: storeData.City || '',
        province: storeData.Prov || '',
        postalCode: storeData.PostalCode || '',
        email: storeData.EmailAdddress || storeData.EmailAddress || storeData.email || '',
        phoneNumber: storeData.Telephone || '',
        
        // Editable fields (initialize with defaults)
        poNumber: '',
        orderDate: new Date().toISOString().split('T')[0], // Today's date
        deliveryDate: '',
        customerOrder: '',
        offCarDelivery: null,
        specialInstructions: ''
      };

      return customerData;
    } catch (error) {
      console.error('Error fetching customer data:', error);
      throw new Error(`Failed to fetch customer data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get mock customer data for development/testing
   * @returns SharedOrderFormData - Mock customer data
   */
  static getMockCustomerData(): SharedOrderFormData {
    return {
      // Autopopulated fields
      customerNumber: '12345',
      storeName: 'McKenzie Garden Center',
      address: '123 Main Street',
      city: 'Toronto',
      province: 'ON',
      postalCode: 'M5V 3A8',
      email: 'info@mckenziegarden.com',
      phoneNumber: '(416) 555-0123',
      
      // Editable fields
      poNumber: '',
      orderDate: new Date().toISOString().split('T')[0], // Today's date
      deliveryDate: '',
      orderType: null,
      specialInstructions: ''
    };
  }
}

/**
 * Hook to fetch customer data with loading and error states
 * @param storeId - The store ID to fetch data for
 * @returns Object with customer data, loading state, and error state
 */
export const useCustomerData = (storeId: string) => {
  const [customerData, setCustomerData] = React.useState<SharedOrderFormData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await CustomerDataService.fetchCustomerData(storeId);
        setCustomerData(data);
      } catch (err) {
        console.error('Error fetching customer data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch customer data');
        
        // Fallback to mock data in case of error
        setCustomerData(CustomerDataService.getMockCustomerData());
      } finally {
        setLoading(false);
      }
    };

    if (storeId) {
      fetchData();
    } else {
      setLoading(false);
      setError('No store ID provided');
    }
  }, [storeId]);

  return { customerData, loading, error };
};
